import { OptionType, SelectInputField } from '@oberoninternal/travelbase-ds/components/form/SelectInput';
import { useThemeContext } from '@oberoninternal/travelbase-ds/hooks/useThemeContext';
import Cross from '@oberoninternal/travelbase-ds/components/figure/Cross';
import Package from '@oberoninternal/travelbase-ds/svg/Package';
import { Box } from '@rebass/grid';
import isEqual from 'lodash.isequal';
import React, { FC, forwardRef, Ref, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import Skeleton from 'react-loading-skeleton';
import searchParams from '../constants/searchParams';
import { SpecialDocument, SpecialQuery, SpecialQueryVariables } from '../generated/graphql';
import StyledCaret from './designsystem/StyledCaret';
import Tag from './svg/Tag';
import useQueryParams from '../hooks/useQueryParams';
import { useApolloClient } from '@apollo/client';

// TODO: Ask BE for a specials query with ids as a param
const useSpecialsQuery = (ids: string[]) => {
    const client = useApolloClient();
    const [state, setState] = useState<{
        loading: boolean;
        data: Exclude<SpecialQuery['special'], undefined | null>[] | null;
    }>({
        loading: true,
        data: null,
    });

    const idsRef = useRef<string[] | null>(null);

    useEffect(() => {
        if (!isEqual(idsRef.current, ids)) {
            const requests = [];

            for (const id of ids) {
                if (!id) {
                    break;
                }
                requests.push(
                    client.query<SpecialQuery, SpecialQueryVariables>({ query: SpecialDocument, variables: { id } })
                );
            }

            Promise.all(requests)
                .then(data =>
                    setState({
                        loading: false,
                        data: data
                            .map(result => result.data.special)
                            .filter((result): result is Exclude<SpecialQuery['special'], undefined | null> => !!result),
                    })
                )
                .catch(err => {
                    // eslint-disable-next-line no-console
                    console.error(err);
                });
            idsRef.current = ids;
        }
    }, [client, ids]);

    return state;
};

interface Props {
    specialIds: string[];
    beforeOnChange?: () => void;
    ref?: Ref<HTMLDivElement>;
}

export const LOWEST_PRICE = 'lowest_price';

const SpecialFilterInput: FC<React.PropsWithChildren<Props>> = forwardRef(({ specialIds, beforeOnChange }, ref) => {
    const theme = useThemeContext();
    const intl = useIntl();
    const defaultOption = {
        label: intl.formatMessage({
            defaultMessage: 'Laagste prijs',
        }),
        value: LOWEST_PRICE,
        icon: <Tag className="gt-s" width={18} height={18} />,
    };
    const [{ specialId = defaultOption.value }, setParams] = useQueryParams(searchParams);
    const { data, loading } = useSpecialsQuery(
        specialIds.concat(
            specialIds.every(id => id !== specialId) && specialId !== defaultOption.value ? [specialId] : []
        )
    );

    if (loading) {
        return (
            <Box width="30rem" height="2rem">
                <Skeleton width="100%" height="100%" />
            </Box>
        );
    }

    const options: OptionType[] = [
        defaultOption,
        ...(data ? data.map(special => ({ label: special.name, value: special.id, icon: <Package /> })) : []),
    ];

    const Toggle = () =>
        specialId !== defaultOption.value ? (
            <Cross
                width={10}
                height={10}
                style={{ marginRight: '0.4rem', cursor: 'pointer' }}
                onTouchEnd={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    setParams({ specialId: undefined }, 'replaceIn');
                }}
                onMouseDown={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    setParams({ specialId: undefined }, 'replaceIn');
                }}
            />
        ) : (
            <StyledCaret />
        );

    if (options.length <= 1) {
        return null;
    }

    return (
        <Box width={[1, null, '20rem', '30rem']} ref={ref}>
            <SelectInputField
                onChange={option => {
                    if (specialId !== (option as OptionType).value) {
                        beforeOnChange?.();
                    }
                    setTimeout(() =>
                        setParams(
                            {
                                specialId:
                                    (option as OptionType).value === LOWEST_PRICE
                                        ? undefined
                                        : (option as OptionType).value,
                            },
                            'replaceIn'
                        )
                    ); // 😔
                }}
                styles={{
                    singleValue: (styles, { data: { value } }) => ({
                        ...styles,
                        fontWeight: value !== defaultOption.value ? 400 : styles.fontWeight,
                        color: value !== defaultOption.value ? theme.colors.neutral['90'] : styles.color,
                    }),
                    dropdownIndicator: (base, state) => ({
                        ...base,
                        color: state.isDisabled
                            ? theme.colors.neutral['20']
                            : state.isFocused
                            ? theme.colors.neutral['90']
                            : theme.colors.neutral['40'],
                        '&:hover': {
                            color: theme.colors.neutral['80'],
                        },
                    }),
                }}
                isDisabled={options.length === 1}
                hideChoices
                value={options.find(option => option.value === specialId) ?? options[0]}
                thinShadow
                rounded
                options={options}
                ToggleComponent={Toggle}
            />
        </Box>
    );
});

export default SpecialFilterInput;
